import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";

export default function AccountManagementTab() {
  const { user } = useAuth();

  // Change Email
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState("");
  const [emailError, setEmailError] = useState("");

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");
    setEmailSuccess("");

    if (!newEmail.trim()) {
      setEmailError("Vui lòng nhập email mới.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      setEmailError("Email không đúng định dạng.");
      return;
    }

    if (newEmail.trim() === user?.email) {
      setEmailError("Email mới không được trùng với email hiện tại.");
      return;
    }

    setEmailLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail.trim() });

      if (error) {
        setEmailError(error.message);
      } else {
        setEmailSuccess(`Đã gửi yêu cầu đổi email. Vui lòng kiểm tra hộp thư ${newEmail.trim()} để xác nhận.`);
        setNewEmail("");
      }
    } catch {
      setEmailError("Có lỗi xảy ra. Vui lòng thử lại.");
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-heading text-lg font-bold text-foreground-950">Quản lý tài khoản</h3>
        <p className="text-xs text-foreground-500 mt-0.5">Thay đổi email tài khoản doanh nghiệp</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Change Email */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-xl">
              <i className="ri-mail-line text-primary-500 text-lg"></i>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-foreground-900">Đổi email</h4>
              <p className="text-xs text-foreground-500">Email hiện tại: {user?.email}</p>
            </div>
          </div>

          <form onSubmit={handleChangeEmail} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Email mới</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="email-moi@doanhnghiep.com"
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>

            {emailError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                <i className="ri-error-warning-line text-base flex-shrink-0"></i>
                <span>{emailError}</span>
              </div>
            )}
            {emailSuccess && (
              <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
                <i className="ri-checkbox-circle-line text-base flex-shrink-0 mt-0.5"></i>
                <span>{emailSuccess}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={emailLoading}
              className="w-full py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {emailLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  <span>Đang xử lý...</span>
                </>
              ) : (
                "Cập nhật email"
              )}
            </button>
          </form>
        </div>

        {/* OTP Login Info */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 flex items-center justify-center bg-accent-50 rounded-xl">
              <i className="ri-smartphone-line text-accent-500 text-lg"></i>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-foreground-900">Đăng nhập bằng mã OTP</h4>
              <p className="text-xs text-foreground-500">Không cần mật khẩu</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-accent-100 rounded-full">
                <i className="ri-mail-check-line text-accent-600 text-base"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-800">Bảo mật hơn</p>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  Mỗi lần đăng nhập hệ thống gửi một mã xác thực 6 chữ số riêng đến email của bạn. Không cần ghi nhớ mật khẩu.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-accent-100 rounded-full">
                <i className="ri-time-line text-accent-600 text-base"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-800">Mã có hiệu lực ngắn</p>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  Mã OTP chỉ có hiệu lực trong thời gian ngắn, giúp ngăn chặn việc đăng nhập trái phép.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}